import {Injectable} from "@angular/core";
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import {exhaustMap, Observable, of, switchMap, withLatestFrom} from "rxjs";
import {catchError, filter, map} from "rxjs/operators";
import {
  createSavedReportsFolder,
  createSavedReportsFolderSuccess,
  deleteSavedReportsFolder,
  deleteSavedReportsFolderFailure,
  deleteSavedReportsFolderSuccess,
  initFolderComponentObjects,
  initFolderComponentObjectsSuccess,
  loadSavedReportsFolders,
  loadSavedReportsFoldersFailure,
  loadSavedReportsFoldersSuccess,
  updateSavedReportsFolder,
  updateSavedReportsFolderFailure,
  updateSavedReportsFolderSuccess
} from "./savedReportsFolders.actions";
import {SavedReportFoldersService} from "../../services/saved-report-folders";
import {Serializer, SerializerResponse} from "../../interfaces/serializer";
import {SavedReportFolder} from "../../interfaces/saved-report-folder";
import {AppState} from "../store";
import { Store } from "@ngrx/store";
import {
  selectFolderComponentObjects,
  selectReportsFoldersLoaded
} from './savedReportsFolders.selectors';
import {FolderComponentObjects} from "../../classes/folder-component-object";
import {SavedReportComponentObject, SavedReportComponentObjects} from "../../classes/saved-report-component-object";
import {selectSavedReportComponentObjects, selectSavedReportsLoaded} from '../savedReports/savedReports.selectors';
import {
  createSavedReportsSuccess,
  deleteSavedReportsSuccess,
  loadSavedReportsSuccess,
  updateSavedReportsStandaloneSuccess,
  updateSavedReportsSuccess
} from '../savedReports/savedReports.actions';
import {TypedAction} from '@ngrx/store/src/models';

@Injectable()
export class SavedReportsFoldersEffects {

  public readonly loadSavedReportsFolders$ = createEffect(() => this._actions$.pipe(
    ofType(loadSavedReportsFolders),
    switchMap(() => this._savedReportsFolderS.getSavedReportFolders().pipe(
      map((savedReportsFolders) => loadSavedReportsFoldersSuccess(savedReportsFolders.data)),
      catchError(() => of(loadSavedReportsFoldersFailure()))
    ))
  ));

  public readonly createSavedReportsFolder$ = createEffect(() => this._actions$.pipe(
    ofType(createSavedReportsFolder),
    exhaustMap((folderData) => this._savedReportsFolderS.createSavedReportFolder(folderData).pipe(
      map((savedReportsFolder: SerializerResponse<Serializer<SavedReportFolder>>) => createSavedReportsFolderSuccess(savedReportsFolder.data)),
      catchError(() => of(loadSavedReportsFoldersFailure()))
    ))
  ));

  public readonly updateSavedReportsFolder$ = createEffect(() => this._actions$.pipe(
    ofType(updateSavedReportsFolder),
    exhaustMap((action) => this._savedReportsFolderS.updateSavedReportFolder(action.savedReportFolder.id, action.update).pipe(
      map((savedReportsFolder: SerializerResponse<Serializer<SavedReportFolder>>) => updateSavedReportsFolderSuccess({ savedReportFolder: savedReportsFolder.data })),
      catchError(() => of(updateSavedReportsFolderFailure()))
    ))
  ));

  public readonly deleteSavedReportsFolder$ = createEffect(() => this._actions$.pipe(
    ofType(deleteSavedReportsFolder),
    exhaustMap((params) => this._savedReportsFolderS.deleteSavedReportFolder(params.folderId).pipe(
      map(() => deleteSavedReportsFolderSuccess(params.folderId)),
      catchError(() => of(deleteSavedReportsFolderFailure()))
    ))
  ));

  //init
  public readonly canInitFolderComponentObjects$: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(
      loadSavedReportsFoldersSuccess,
      createSavedReportsFolderSuccess,
      updateSavedReportsFolderSuccess,
      deleteSavedReportsFolderSuccess,
      loadSavedReportsSuccess,
      createSavedReportsSuccess,
      updateSavedReportsSuccess,
      deleteSavedReportsSuccess,
      updateSavedReportsStandaloneSuccess
    ),
    withLatestFrom(
      this._store.select(selectReportsFoldersLoaded),
      this._store.select(selectSavedReportsLoaded),
    ),
    filter(([_, foldersLoaded, reportsLoaded]: [TypedAction<string>, boolean, boolean]) => foldersLoaded && reportsLoaded),
    map(() => initFolderComponentObjects())
  ));

  public readonly initFolderComponentObjects$: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(initFolderComponentObjects),
    withLatestFrom(
      this._store.select(selectFolderComponentObjects),
      this._store.select(selectSavedReportComponentObjects)
    ),
    map(([_, folderComponentObjects, reportComponentObjects]: [TypedAction<string>, FolderComponentObjects, SavedReportComponentObjects]) => {
      for (const folder of folderComponentObjects) {
        folder.init(folderComponentObjects, reportComponentObjects.filter((reportComponentObject: SavedReportComponentObject): boolean => reportComponentObject.payload.attributes.authorize));
      }

      return initFolderComponentObjectsSuccess();
    })
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _actions$: Actions,
    private readonly _savedReportsFolderS: SavedReportFoldersService
  ) {}
}
